import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Download, Paperclip } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { DetailSkeleton } from "@/components/skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Heading } from "@/components/ui/typography";
import { classificationLabel } from "@/lib/inbound-email";
import { serverUrl } from "@/utils/server-url";
import { trpc, withErrorToast } from "@/utils/trpc";

function formatReceived(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return format(date, "d MMM yyyy, HH:mm");
}

// Wrap sanitized HTML in a locked-down document. The iframe is sandboxed (no
// scripts, no same-origin) and a CSP blocks every network fetch, so remote
// trackers / images can't phone home and nothing can script the parent.
function buildBodyDoc(html: string) {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:;"><style>body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;margin:12px;word-break:break-word}</style></head><body>${html}</body></html>`;
}

export default function EmailDetail() {
  const { id } = useParams<{ id: string }>();
  const email = useQuery({
    ...withErrorToast(trpc.inboundEmails.get.queryOptions({ id: id || "" })),
    enabled: Boolean(id),
  });
  const body = useQuery({
    ...trpc.inboundEmails.getBody.queryOptions({ id: id || "" }),
    enabled: Boolean(id),
  });

  if (email.isLoading) {
    return (
      <PageContainer className="space-y-6">
        <div>
          <Skeleton className="mb-2 h-7 w-32" />
          <Skeleton className="h-9 w-72 max-w-full" />
        </div>
        <DetailSkeleton />
      </PageContainer>
    );
  }

  if (email.isError || !email.data) {
    return (
      <PageContainer>
        <div className="text-sm text-destructive">Email not found.</div>
      </PageContainer>
    );
  }

  const data = email.data;

  return (
    <PageContainer className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
          <Link to="/email">
            <ArrowLeft className="h-4 w-4" />
            Back to email
          </Link>
        </Button>
        <Heading variant="page">{data.subject || "(no subject)"}</Heading>
      </div>

      <div className="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-start">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <MetaRow label="Classification">
              {classificationLabel(data.classification) ? (
                <Badge variant="secondary">
                  {classificationLabel(data.classification)}
                </Badge>
              ) : (
                <span className="text-muted-foreground">
                  Not yet classified
                </span>
              )}
            </MetaRow>
            {data.summary ? (
              <MetaRow label="Summary">{data.summary}</MetaRow>
            ) : null}
            <MetaRow label="From">{data.from ?? "Unknown sender"}</MetaRow>
            <MetaRow label="To">{data.to ?? "—"}</MetaRow>
            <MetaRow label="Received">
              {formatReceived(data.receivedAt)}
            </MetaRow>
            <MetaRow label="Site">
              {data.siteId ? (
                <Link
                  to={`/sites/${data.siteId}`}
                  className="font-medium text-foreground underline underline-offset-4"
                >
                  {data.siteName ?? "View site"}
                </Link>
              ) : (
                <span className="text-muted-foreground">Not linked</span>
              )}
            </MetaRow>
            {data.entranceName ? (
              <MetaRow label="Entrance">{data.entranceName}</MetaRow>
            ) : null}
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Message</CardTitle>
            </CardHeader>
            <CardContent>
              {body.isLoading ? (
                <div className="space-y-2.5">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-11/12" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : body.data?.html ? (
                <iframe
                  title="Email body"
                  sandbox=""
                  className="h-[28rem] w-full rounded-md border border-border bg-white"
                  srcDoc={buildBodyDoc(body.data.html)}
                />
              ) : body.data?.text ? (
                <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">
                  {body.data.text}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No readable message body.
                </p>
              )}
            </CardContent>
          </Card>

          {data.attachments.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Attachments ({data.attachments.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.attachments.map((attachment) => (
                  <a
                    key={attachment.index}
                    href={`${serverUrl}/api/inbound-emails/${data.id}/attachments/${attachment.index}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm transition hover:bg-muted"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Paperclip className="h-4 w-4 shrink-0" />
                      <span className="truncate">{attachment.filename}</span>
                    </span>
                    <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </a>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </PageContainer>
  );
}

function MetaRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="break-words text-sm text-foreground">{children}</div>
    </div>
  );
}
